// src/components/chat/MessageComposer.jsx
// Chat message composer — textarea + emoji picker + send button + edit mode + @mentions.
import React, { useRef, useState, useEffect, useCallback } from 'react';
import EmojiPicker from 'emoji-picker-react';
import MentionAutocomplete from './MentionAutocomplete';

export default function MessageComposer({
  value,
  onChange,
  onSend,
  onTyping,
  disabled,
  replyingTo,
  onCancelReply,
  editingMessage,
  onCancelEdit,
  isTeamRoom,
  roomMembers,
}) {
  const textareaRef = useRef(null);
  const pickerRef = useRef(null);
  const btnRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // @mention autocomplete state
  const [mentionState, setMentionState] = useState(null); // { startIndex, filter }

  // Close picker when clicking outside
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClick = (e) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showEmojiPicker]);

  const handleEmojiClick = useCallback((emojiData) => {
    const emoji = emojiData.emoji;
    const ta = textareaRef.current;

    if (ta) {
      const start = ta.selectionStart ?? value.length;
      const end = ta.selectionEnd ?? value.length;
      const next = value.slice(0, start) + emoji + value.slice(end);
      onChange(next);

      // Restore cursor position after the inserted emoji
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const pos = start + emoji.length;
          textareaRef.current.selectionStart = pos;
          textareaRef.current.selectionEnd = pos;
          textareaRef.current.focus();
        }
      });
    } else {
      onChange(value + emoji);
    }
  }, [value, onChange]);

  // Auto-focus textarea when reply mode or edit mode is activated
  useEffect(() => {
    if ((replyingTo || editingMessage) && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [replyingTo, editingMessage]);

  // Detect @mention trigger on every value/cursor change (team rooms only)
  const detectMention = useCallback(() => {
    if (!isTeamRoom || !Array.isArray(roomMembers) || roomMembers.length === 0) {
      if (mentionState) setMentionState(null);
      return;
    }

    const ta = textareaRef.current;
    if (!ta) return;

    const cursorPos = ta.selectionStart;
    const textBefore = value.slice(0, cursorPos);

    // Look for the last '@' before the cursor that isn't preceded by a word character
    const atIndex = textBefore.lastIndexOf('@');
    if (atIndex === -1) {
      if (mentionState) setMentionState(null);
      return;
    }

    // '@' must be at start of string or preceded by whitespace
    if (atIndex > 0 && !/\s/.test(textBefore[atIndex - 1])) {
      if (mentionState) setMentionState(null);
      return;
    }

    // Text between '@' and cursor must not contain newlines
    const partial = textBefore.slice(atIndex + 1);
    if (partial.includes('\n')) {
      if (mentionState) setMentionState(null);
      return;
    }

    // Check if the partial text matches at least one member name (prefix match).
    // If no members match, dismiss the autocomplete so Enter/send works normally.
    const lowerPartial = partial.toLowerCase();
    const hasMatch = roomMembers.some((m) =>
      (m.displayName || '').toLowerCase().startsWith(lowerPartial)
    );
    if (!hasMatch) {
      if (mentionState) setMentionState(null);
      return;
    }

    setMentionState({ startIndex: atIndex, filter: partial });
  }, [value, isTeamRoom, roomMembers, mentionState]);

  // Handle @mention selection
  const handleMentionSelect = useCallback((member) => {
    if (!mentionState) return;
    const name = member.displayName || member.userId || '';
    const before = value.slice(0, mentionState.startIndex);
    const after = value.slice(mentionState.startIndex + 1 + mentionState.filter.length);
    const newValue = `${before}@${name} ${after}`;
    onChange(newValue);
    setMentionState(null);

    // Set cursor after the inserted mention
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = mentionState.startIndex + 1 + name.length + 1; // @name + space
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    });
  }, [mentionState, value, onChange]);

  const handleMentionClose = useCallback(() => {
    setMentionState(null);
  }, []);

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* Edit preview strip */}
      {editingMessage && (
        <div className="px-4 pt-3 pb-0 flex items-start gap-2">
          <div className="flex-1 pl-3 border-l-2 border-yellow-400 rounded-sm min-w-0">
            <div className="text-xs font-medium text-yellow-600 truncate">
              Editing message
            </div>
            <p className="text-xs text-gray-500 truncate">
              {(editingMessage.body || '').slice(0, 120)}
            </p>
          </div>
          <button
            type="button"
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5"
            title="Cancel edit"
            onClick={onCancelEdit}
          >
            {'\u2715'}
          </button>
        </div>
      )}

      {/* Reply preview strip */}
      {replyingTo && !editingMessage && (
        <div className="px-4 pt-3 pb-0 flex items-start gap-2">
          <div className="flex-1 pl-3 border-l-2 border-[#00aef7] rounded-sm min-w-0">
            <div className="text-xs font-medium text-[#00aef7] truncate">
              {replyingTo.senderName || 'Message'}
            </div>
            <p className="text-xs text-gray-500 truncate">
              {(replyingTo.body || '').slice(0, 120)}
            </p>
          </div>
          <button
            type="button"
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5"
            title="Cancel reply"
            onClick={onCancelReply}
          >
            {'\u2715'}
          </button>
        </div>
      )}

      <div className="p-4 flex gap-2 items-end relative">
        {/* Emoji picker toggle */}
        <button
          ref={btnRef}
          type="button"
          className={
            'w-9 h-9 flex items-center justify-center rounded-full text-lg transition-colors ' +
            (showEmojiPicker
              ? 'bg-[#00aef7]/10 text-[#00aef7]'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100')
          }
          title="Emoji"
          onClick={() => setShowEmojiPicker((v) => !v)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
        </button>

        {/* Emoji picker popover */}
        {showEmojiPicker && (
          <div
            ref={pickerRef}
            className="absolute bottom-14 left-0 z-50"
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              width={280}
              height={350}
              emojiStyle="native"
              searchPlaceholder="Search emoji..."
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              lazyLoadEmojis
            />
          </div>
        )}

        {/* @mention autocomplete (positioned above textarea) */}
        {mentionState && isTeamRoom && Array.isArray(roomMembers) && roomMembers.length > 0 && (
          <MentionAutocomplete
            members={roomMembers}
            filter={mentionState.filter}
            onSelect={handleMentionSelect}
            onClose={handleMentionClose}
          />
        )}

        <textarea
          ref={textareaRef}
          rows={3}
          placeholder={editingMessage ? 'Edit your message...' : 'Type a message.'}
          className="flex-1 border border-gray-300 rounded-2xl px-4 py-2 focus:outline-none focus:border-[#00aef7] resize-none leading-5 h-[82px] overflow-y-auto"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            onTyping?.();
            requestAnimationFrame(() => {
              if (textareaRef.current) {
                textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
              }
            });
            // Detect @mention after state update
            requestAnimationFrame(() => detectMention());
          }}
          onKeyDown={(e) => {
            // Don't handle Enter/Escape when mention autocomplete is open
            if (mentionState) return;

            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
            if (e.key === 'Escape') {
              if (editingMessage) {
                e.preventDefault();
                onCancelEdit?.();
              } else if (replyingTo) {
                e.preventDefault();
                onCancelReply?.();
              }
            }
          }}
          onClick={() => {
            // Re-detect mention on cursor position change
            requestAnimationFrame(() => detectMention());
          }}
          onKeyUp={() => {
            // Re-detect mention on cursor movement (arrow keys etc.)
            requestAnimationFrame(() => detectMention());
          }}
        />

        <button
          type="button"
          className="bg-[#00aef7] text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-[#099ad9] disabled:bg-gray-300 disabled:cursor-not-allowed"
          title={editingMessage ? 'Save edit' : 'Send'}
          onClick={onSend}
          disabled={disabled || !(value && value.trim())}
        >
          {editingMessage ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          ) : (
            '\u2191'
          )}
        </button>
      </div>
    </div>
  );
}
